import { FormikProps, useFormik } from 'formik'
import {
  addDays,
  differenceInDays,
  format,
  parseISO,
  startOfDay,
} from 'date-fns'
import {
  ButtonV2,
  DatePickerV2,
  LabraToolTipV2,
  labraUIDateFormat,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
  RadioButtonsV2,
  RadioButtonType,
  useAuth,
} from '@labrav/react-components'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { DateTime } from 'luxon'
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import * as yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarPlus } from '@fortawesome/pro-regular-svg-icons'
import {
  expirationDateValidationErrorMsg,
  subscriptionEndDateValidationErrorMsg,
  providerToAuthCloud,
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import { useDispatch, useSelector } from '../../../store'
import { updatePrivateOfferExpiryDate } from '../../../modules/privateOffer/action'
import {
  OfferStatus,
  OfferTypeRenew,
  Subscription,
} from '../../../modules/privateOffer/types'
import { isOfferExpired } from '../../utils/isOfferExpired'
import {
  allowedPlanTypesForUpdateExpiryDate,
  updateOfferExpiringToolTipMessage,
} from '../../../utils/constants'
import { DetailInfo } from '../DetailRow/DetailRow'
import { CloudMarketplace } from '../../../modules/productsListing/types'
import { useStyles } from './ExtendPrivateOfferExpiration.styles'

export type UpdatePrivateOfferExpirationProps = {
  offerExpirationDate: string
  offerSubscriptionEndDate: string
  offerId: string
  planType: PlanType
  offerStatus?: OfferStatus
  isDraft?: boolean
  aggrementStartDate?: string | null
  type?: OfferTypeRenew
  labraSubscriptionId?: string | null
  subscription?: Subscription
  partnerId?: string
  cloudMarketplace?: CloudMarketplace
}

export const ExtendPrivateOfferExpiration = React.memo(
  ({
    offerExpirationDate,
    offerId,
    planType,
    offerSubscriptionEndDate,
    offerStatus,
    isDraft,
    aggrementStartDate,
    type,
    labraSubscriptionId,
    subscription,
    partnerId,
    cloudMarketplace: cloudMarketplaceProp,
  }: UpdatePrivateOfferExpirationProps) => {
    const { classes } = useStyles()
    const theme = useTheme()
    const dispatch = useDispatch()
    const selectedCloudType = useCurrentCloudProvider()
    const subscriptionEndDate = DateTime.fromISO(offerSubscriptionEndDate)
    const subscriptionEndDateString = subscriptionEndDate.toISODate()
    const formattedAgreementStartDate = aggrementStartDate
      ? DateTime.fromISO(aggrementStartDate).toFormat('dd-MM-yyyy')
      : undefined

    const loading = useSelector(state =>
      isLoading(state.loading)(LoadingTypes.UPDATE_FLYOUT_OFFER)
    )
    const [openExpiryDateModal, setExpiryDateModal] = useState<boolean>(false)
    const [isCustomExpirationDate, setIsCustomExpirationDate] =
      useState<boolean>(true)

    const isAWSRenewalOffer =
      type === OfferTypeRenew.RENEWAL && !isEmpty(labraSubscriptionId)

    const fields: FormField[] = [
      {
        title: 'New offer expiration date',
        humanReadableKey: 'New offer expiration date',
        placeHolder: 'New offer expiration date',
        id: 'offerExpirationDate',
        isRequired: true,
        isReadOnly: !isCustomExpirationDate,
        isCustom: false,
        dataType: 'date',
        minDate: 'today',
      },
    ]
    const extendByDaysRadioButtonMapping = [
      {
        key: '5',
        label: 'By 5 days',
        type: RadioButtonType.STANDARD,
      },
      {
        key: '10',
        label: 'By 10 days',
        type: RadioButtonType.STANDARD,
      },
      {
        key: '15',
        label: 'By 15 days',
        type: RadioButtonType.STANDARD,
      },
      {
        key: 'custom',
        label: 'Custom',
        type: RadioButtonType.STANDARD,
      },
    ]
    const initialValue: Record<string, unknown> = {
      offerExpirationDate: '',
      subscriptionEndDate: subscriptionEndDateString,
    }

    const validateSchema = yup.object().shape({
      offerExpirationDate: yup
        .string()
        .required(expirationDateValidationErrorMsg)
        .typeError(expirationDateValidationErrorMsg)
        .test('update-expiration-date', function (value) {
          if (aggrementStartDate) {
            const agreementStart = DateTime.fromISO(aggrementStartDate)
            const expirationDate = DateTime.fromISO(value || '')
            if (!(expirationDate < agreementStart)) {
              return this.createError({
                message: `Offer Expiration Date should be smaller than Specified Start Date (${formattedAgreementStartDate}).`,
              })
            }
          }
          if (isAWSRenewalOffer) {
            const expirationDate = DateTime.fromISO(value || '')
            const expectedExpirationDate = DateTime.fromISO(
              subscription?.expectedExpirationDate || ''
            )
            const offerSubscriptionEndDate = DateTime.fromISO(
              this.parent.subscriptionEndDate || ''
            )
            const comparisonDate =
              expectedExpirationDate < offerSubscriptionEndDate
                ? expectedExpirationDate
                : offerSubscriptionEndDate

            const formattedComparisonDate =
              comparisonDate.toFormat('dd-MM-yyyy')
            const message = comparisonDate.equals(expectedExpirationDate)
              ? `Offer Expiration Date should be smaller than or equal to the previous service end date (${formattedComparisonDate}).`
              : `Offer Expiration Date should be smaller than or equal to the service end date (${formattedComparisonDate}).`
            if (!(expirationDate <= comparisonDate)) {
              return this.createError({
                message: message,
              })
            }
          }
          return true
        }),
      subscriptionEndDate: yup
        .string()
        .nullable()
        .test(
          'check offer subscription end date',
          subscriptionEndDateValidationErrorMsg,
          function (value?: string | null) {
            if (!isEmpty(value)) {
              if (isAWSRenewalOffer || aggrementStartDate) {
                return true
              }
              return (
                DateTime.fromISO(this.parent.offerExpirationDate) <
                DateTime.fromISO(value || '')
              )
            }
            return true
          }
        ),
    })

    const formik = useFormik({
      initialValues: initialValue,
      validationSchema: validateSchema,
      validateOnBlur: true,
      validateOnChange: true,
      onSubmit: () => void 0,
    })
    const handleOpenModal = useCallback(() => {
      formik.resetForm({ values: initialValue })
      setExpiryDateModal(true)
    }, [formik, initialValue])
    const handleClose = useCallback(() => {
      formik.resetForm()
      setExpiryDateModal(false)
    }, [formik])
    const finalFields: FormField[] = useMemo(() => {
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        fields.push({
          title: 'Subscription end date',
          humanReadableKey: 'Subscription end date',
          placeHolder: 'Subscription end date',
          id: 'subscriptionEndDate',
          isRequired: true,
          isReadOnly: false,
          isCustom: false,
          defaultValue: subscriptionEndDateString || undefined,
          dataType: 'date',
          minDate: !isEmpty(formik.values.offerExpirationDate)
            ? DateTime.fromISO(formik.values.offerExpirationDate as string, {
                zone: 'UTC',
              })
                .plus({ day: 1 })
                .toString()
            : DateTime.now().toString(),
        })
      }
      return [...fields]
    }, [planType, formik.values.offerExpirationDate])
    const isOfferExpirationDateEmpty = isEmpty(
      formik.values.offerExpirationDate as string
    )
    const disabled = useMemo(() => {
      return (
        isOfferExpirationDateEmpty ||
        !isEmpty(formik.errors.offerExpirationDate) ||
        !isEmpty(formik.errors?.subscriptionEndDate || '')
      )
    }, [
      isOfferExpirationDateEmpty,
      formik.errors.offerExpirationDate,
      formik.errors?.subscriptionEndDate,
    ])
    const disabledAcceptButtonMessage = useMemo(() => {
      if (isOfferExpirationDateEmpty) {
        return 'Please select new expiration date.'
      }
      if (!isEmpty(formik.errors.offerExpirationDate)) {
        return String(formik.errors.offerExpirationDate)
      }
      if (!isEmpty(formik.errors?.subscriptionEndDate)) {
        return String(formik.errors.subscriptionEndDate)
      }
      return ''
    }, [
      isOfferExpirationDateEmpty,
      formik.errors.offerExpirationDate,
      formik.errors?.subscriptionEndDate,
    ])
    const handleAccept = useCallback(async () => {
      setExpiryDateModal(false)
      const dataToBeSent: Record<string, unknown> = {
        offerExpirationDate:
          selectedCloudType !== 'GCP'
            ? DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISODate()
            : DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISO(),
      }
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        dataToBeSent['subscriptionEndDate'] = DateTime.fromISO(
          formik.values.subscriptionEndDate as string
        ).toISODate()
      }
      if (selectedCloudType === 'GCP') {
        dataToBeSent['offerStatus'] = offerStatus
      }
      await dispatch(
        updatePrivateOfferExpiryDate(
          offerId,
          { ...dataToBeSent },
          undefined,
          undefined,
          partnerId,
          cloudMarketplaceProp || selectedCloudType
        )
      )
    }, [
      formik.values.offerExpirationDate,
      formik.values.subscriptionEndDate,
      planType,
      selectedCloudType,
      offerStatus,
      dispatch,
      offerId,
      partnerId,
      cloudMarketplaceProp,
    ])
    const isExpired = isOfferExpired(new Date(offerExpirationDate))

    const updateOfferStatusCheck =
      (offerStatus === OfferStatus?.PRIVATE_OFFER_CREATION_IN_PROGRESS &&
        !isDraft) ||
      offerStatus === OfferStatus?.PRIVATE_OFFER_FAILED ||
      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS ||
      offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED ||
      (isExpired &&
        planType &&
        !allowedPlanTypesForUpdateExpiryDate.includes(planType))

    const { loading: authLoading, canDo } = useAuth()
    const selectedAuthCloud = useCurrentCloudProvider()
    const canWriteMarketplace = useMemo(
      () =>
        !authLoading &&
        canDo({
          cloud: providerToAuthCloud(selectedAuthCloud.toLowerCase()),
          service: 'marketplace',
          action: 'update',
        }),
      [authLoading, canDo, selectedAuthCloud]
    )

    function setExpirationDate(daysToAdd: number) {
      if (offerExpirationDate) {
        const newExpirationDate = format(
          addDays(new Date(), daysToAdd),
          'yyyy-MM-dd'
        )
        formik.setFieldValue('offerExpirationDate', newExpirationDate)
        setIsCustomExpirationDate(false)
      }
    }

    function handleSelection(event: ChangeEvent<HTMLInputElement>): number {
      const selectedDuration = event.target.value as string

      switch (selectedDuration) {
        case '5':
        case '10':
        case '15':
          setExpirationDate(parseInt(selectedDuration, 10))
          setIsCustomExpirationDate(true)
          return parseInt(selectedDuration, 10)

        case 'custom':
          return 0
        default:
          return 0
      }
    }

    const checkedValue = useMemo(() => {
      const dateValue = formik.values.offerExpirationDate as string
      if (isEmpty(dateValue)) {
        return ''
      }
      const formikDate = startOfDay(parseISO(dateValue))
      const today = startOfDay(new Date())
      const diffInDays = differenceInDays(formikDate, today)

      switch (diffInDays) {
        case 5:
          return '5'
        case 10:
          return '10'
        case 15:
          return '15'
        default:
          return 'custom'
      }
    }, [formik.values.offerExpirationDate])

    const tooltipDescription = !canWriteMarketplace ? (
      <>This action is disabled because you have read-only access</>
    ) : updateOfferStatusCheck ? (
      updateOfferExpiringToolTipMessage
    ) : (
      ''
    )

    const isButtonDisabled = updateOfferStatusCheck || !canWriteMarketplace

    return (
      <div className={classes.updateButtonContainer}>
        {isButtonDisabled ? (
          <LabraToolTipV2
            tooltipPlacement="top"
            description={tooltipDescription}
          >
            <ButtonV2
              className={classes.disabledEdit}
              styleType="link"
              data-testid={'extend-expiration-date-disabled'}
              disabled={true}
            >
              <FontAwesomeIcon
                icon={faCalendarPlus}
                style={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  padding: '0px 12px 0px 0px',
                  color: theme.palette.primary.main,
                }}
              />
              Extend expiration date
            </ButtonV2>
          </LabraToolTipV2>
        ) : (
          <ButtonV2
            className={classes.extendOfferExpiry}
            styleType="link"
            data-testid={'extend-expiration-date'}
            onClick={handleOpenModal}
          >
            <FontAwesomeIcon
              icon={faCalendarPlus}
              style={{
                width: '16px',
                height: '16px',
                fontSize: '16px',
                padding: '0px 12px 0px 0px',
                color: theme.palette.primary.main,
              }}
            />
            Extend expiration date
          </ButtonV2>
        )}
        <ModalConfirmV2
          title={
            <>
              {' '}
              <FontAwesomeIcon
                icon={faCalendarPlus}
                style={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  padding: '0px 12px 0px 0px',
                  color: theme.palette.text.primary,
                }}
              />
              Extend expiration date
            </>
          }
          content={
            <>
              <DetailInfo
                title={'Current offer expiration date'}
                value={
                  <div className={classes.customWrapper}>
                    {offerExpirationDate}
                  </div>
                }
                keyName={'offerExpirationDate'}
              />{' '}
              <br></br>
              Extend expiration date by selecting one of the options below or by
              adding a custom date. <br></br>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: checkedValue,
                    value: checkedValue,
                  }}
                  name="offerExpirationDate"
                  radioButtonMappings={extendByDaysRadioButtonMapping}
                  handleChange={handleSelection}
                />
              </div>
              <ShowDateInput formik={formik} fields={finalFields} />
            </>
          }
          open={openExpiryDateModal}
          titleAcceptButton={'Extend expiration date'}
          titleCancelButton={'Cancel'}
          onAccept={handleAccept}
          onDecline={handleClose}
          variant={ModalSizeVariants.medium}
          hideCancelButton={false}
          acceptDisabled={loading || disabled}
          disabledAcceptButtonMessage={disabledAcceptButtonMessage}
        />
      </div>
    )
  }
)

export type ShowDateInputProps = {
  fields: FormField[]
  formik: FormikProps<Record<string, unknown>>
}

export const ShowDateInput = React.memo(
  ({ fields, formik }: ShowDateInputProps) => {
    const { classes } = useStyles()
    return (
      <>
        {fields.map(field => (
          <div className={classes.dateField} key={`div-${field.id}`}>
            <DatePickerV2
              formik={formik}
              format={labraUIDateFormat}
              field={field}
              key={field.id}
              views={['year', 'month', 'day']}
            />
          </div>
        ))}
      </>
    )
  }
)
